import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';

import { catchError, combineLatest, EMPTY, filter, map, Observable, of, switchMap } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { FinnairCancelFlowType } from '@fcom/dapi/api/models/finnair-cancel-flow-type';
import { FinnairOrder } from '@fcom/dapi/api/models/finnair-order';
import { FinnairPassengerItem } from '@fcom/dapi/api/models/finnair-passenger-item';
import { FinnairServiceItem } from '@fcom/dapi/api/models/finnair-service-item';
import { FinnairTotalPricesDetails } from '@fcom/dapi/api/models/finnair-total-prices-details';
import { ServiceFlow } from '@fcom/dapi/api/models/service-flow';
import { ButtonTheme, ModalButtons, NotificationLayout, NotificationTheme } from '@fcom/ui-components';
import { hasUnitPrices } from '@fcom/common/utils/price-utils';
import { MonitoringLogger } from '@fcom/core/services/monitoring/monitoring-logger';
import { asPaxTypeKey } from '@fcom/dapi/utils/pax-details-utils';
import { BreakdownModel } from '@fcom/dapi/interfaces/price.interface';
import { CmsArticle } from '@fcom/core-api';
import { finShare } from '@fcom/rx';
import { FragmentService } from '@fcom/common/services/fragments/fragment.service';

import { getPassengerServices, hasPassengers } from '../../utils/order.utils';
import { RefundDetailNotificationType } from '../../interfaces/refund.interface';
import { CancelViewModel } from '../../../cancel/interfaces';

const DEPRECATED_AND_INVOLUNTARY_RULES = [
  FinnairCancelFlowType.MANUAL_INVOLUNTARY,
  FinnairCancelFlowType.AUTOMATIC_INVOLUNTARY,
];

const NON_REFUNDABLE_EMD_FLOW_TYPES: FinnairCancelFlowType[] = [
  FinnairCancelFlowType.AUTOMATIC_RULE_TICKET_SLIGHTCLASSIC,
  FinnairCancelFlowType.MANUAL_RULE_TICKET_SLIGHTCLASSIC,
  FinnairCancelFlowType.RULE_CACR,
  FinnairCancelFlowType.MANUAL_RULE_TICKET_AWARD,
];

const HIDE_FLIGHT_TICKET_NOTIFICATION_FLOW_TYPES: FinnairCancelFlowType[] = [
  FinnairCancelFlowType.MANUAL_RULE_24_HOUR,
  FinnairCancelFlowType.AUTOMATIC_RULE_24_HOUR,
  FinnairCancelFlowType.MANUAL_DYNAMIC_WAIVER,
  FinnairCancelFlowType.AUTOMATIC_DYNAMIC_WAIVER,
];

const KOREA_COUNTRY_CODE = 'KR';

@Component({
  selector: 'fin-refund-detail',
  templateUrl: './refund-detail.component.html',
  styleUrls: ['./refund-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RefundDetailComponent implements OnInit {
  private monitoringLogger = inject(MonitoringLogger);
  private fragmentService = inject(FragmentService);

  readonly asPaxTypeKey = asPaxTypeKey;
  readonly ModalButtons = ModalButtons;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly ServiceFlow = ServiceFlow;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;

  @Input({ required: true })
  orderViewModel$: Observable<CancelViewModel>;

  @Input({ required: true })
  notificationType: RefundDetailNotificationType;

  @Output()
  goToClaimForm: EventEmitter<void> = new EventEmitter<void>();

  mainNotificationContent$: Observable<CmsArticle>;
  flightTicketNotificationKey$: Observable<string>;
  travelExtrasNotificationKey$: Observable<string>;
  displayFlightTicketNotification$: Observable<boolean>;
  displayRefundNotificationWithLink$: Observable<boolean>;
  displayMainNotification$: Observable<boolean>;
  displayGoToClaimFormButton$: Observable<boolean>;
  displayRefundAmountBlock$: Observable<boolean>;
  displayEligibleForRefundMessage$: Observable<boolean>;
  isDeprecatedOrInvoluntaryRule$: Observable<boolean>;
  travelExtraRefundInfo$: Observable<{
    hasServices: boolean;
    isNonRefundable: boolean;
  }>;
  services$: Observable<FinnairServiceItem[]>;

  summaryModalOpen: boolean;
  summaryModalData: { prices: FinnairTotalPricesDetails };
  breakdowns$: Observable<BreakdownModel[]> = of([]);

  ngOnInit(): void {
    this.services$ = this.orderViewModel$.pipe(map((orderViewModel) => orderViewModel.paidServices));
    this.isDeprecatedOrInvoluntaryRule$ = this.orderViewModel$.pipe(
      map((orderViewModel) => DEPRECATED_AND_INVOLUNTARY_RULES.includes(orderViewModel.flowType)),
      finShare()
    );
    this.breakdowns$ = this.orderViewModel$.pipe(
      filter(
        (orderViewModel) => hasPassengers(orderViewModel.passengers) && hasUnitPrices(orderViewModel.refundAmount, true)
      ),
      map((orderViewModel) => {
        const refundAmount = orderViewModel.refundAmount;
        return orderViewModel.passengers.map((passenger: FinnairPassengerItem) => ({
          ...passenger,
          prices:
            refundAmount.flight?.totalPerPax?.[passenger.id] || refundAmount.services?.totalPerPax?.[passenger.id],
          services: getPassengerServices(refundAmount.services?.totalPerCategory, passenger),
          total: refundAmount.total.totalPerPax?.[passenger.id]?.totalAmount,
        }));
      }),
      catchError((e: unknown) => {
        this.monitoringLogger.error('Exception in handling prices of passengers', e);
        return EMPTY;
      })
    );
    this.displayGoToClaimFormButton$ = this.orderViewModel$.pipe(
      map(
        (orderViewModel) =>
          orderViewModel.flowType === FinnairCancelFlowType.RULE_CACR &&
          this.notificationType === RefundDetailNotificationType.CONFIRMATION
      )
    );
    this.mainNotificationContent$ = this.orderViewModel$.pipe(
      map((orderViewModel) =>
        this.getNotificationKey(orderViewModel.flowType, orderViewModel.order, 'mainNotification')
      ),
      switchMap((key) => this.fragmentService.getFragment<CmsArticle>(key)),
      finShare()
    );
    this.displayFlightTicketNotification$ = combineLatest([
      this.orderViewModel$,
      this.isDeprecatedOrInvoluntaryRule$,
      this.services$,
    ]).pipe(
      map(([orderViewModel, isDeprecatedOrInvoluntary, services]) => {
        return isDeprecatedOrInvoluntary
          ? this.notificationType === RefundDetailNotificationType.REVIEW || !!services?.length
          : !HIDE_FLIGHT_TICKET_NOTIFICATION_FLOW_TYPES.includes(orderViewModel.flowType);
      })
    );
    this.flightTicketNotificationKey$ = this.displayFlightTicketNotification$.pipe(
      filter((displayNotification) => displayNotification),
      switchMap(() =>
        this.orderViewModel$.pipe(
          map((orderViewModel) =>
            this.getNotificationKey(orderViewModel.flowType, orderViewModel.order, 'flightTicketsNotification')
          ),
          finShare()
        )
      )
    );
    this.displayRefundNotificationWithLink$ = combineLatest([
      this.displayFlightTicketNotification$,
      this.isDeprecatedOrInvoluntaryRule$,
    ]).pipe(
      map(
        ([displayFlightNotification, isDeprecatedOrInvoluntary]) =>
          !displayFlightNotification &&
          isDeprecatedOrInvoluntary &&
          this.notificationType === RefundDetailNotificationType.CONFIRMATION
      )
    );
    this.travelExtrasNotificationKey$ = this.orderViewModel$.pipe(
      map((orderViewModel) =>
        this.getNotificationKey(orderViewModel.flowType, orderViewModel.order, 'travelExtrasNotification')
      ),
      finShare()
    );
    this.displayMainNotification$ = combineLatest([this.isDeprecatedOrInvoluntaryRule$, this.services$]).pipe(
      map(
        ([isDeprecatedOrInvoluntary, services]) =>
          !isDeprecatedOrInvoluntary ||
          (services?.length && this.notificationType === RefundDetailNotificationType.CONFIRMATION)
      )
    );
    this.displayEligibleForRefundMessage$ = this.orderViewModel$.pipe(
      map((orderViewModel) => this.displayEligibleForRefundMessage(orderViewModel.flowType))
    );
    this.displayRefundAmountBlock$ = this.orderViewModel$.pipe(
      map(
        (orderViewModel) =>
          orderViewModel.flowType !== FinnairCancelFlowType.MANUAL_RULE_TICKET_AWARD &&
          orderViewModel.flowType !== FinnairCancelFlowType.MANUAL_RULE_24_HOUR_JB_FARE &&
          !!orderViewModel.order
      )
    );
    this.travelExtraRefundInfo$ = combineLatest([this.orderViewModel$, this.services$]).pipe(
      map(([orderViewModel, services]) => ({
        hasServices: services?.length > 0,
        isNonRefundable: NON_REFUNDABLE_EMD_FLOW_TYPES.includes(orderViewModel.flowType),
      })),
      finShare()
    );
  }

  openSummaryModal = (event: Event, prices: FinnairTotalPricesDetails): void => {
    event.stopPropagation();

    this.summaryModalOpen = true;
    this.summaryModalData = {
      prices,
    };
  };

  closeSummaryModal = (): void => {
    this.summaryModalData = null;
  };

  onGoToClaimForm(): void {
    this.goToClaimForm.emit();
  }

  private getNotificationKey = (
    flow: FinnairCancelFlowType,
    order: FinnairOrder,
    notificationContext: 'mainNotification' | 'flightTicketsNotification' | 'travelExtrasNotification'
  ): string => {
    const contentFormat = notificationContext === 'mainNotification' ? '.url' : '';

    switch (flow) {
      case FinnairCancelFlowType.AUTOMATIC_RULE_24_HOUR:
      case FinnairCancelFlowType.MANUAL_RULE_24_HOUR:
      case FinnairCancelFlowType.MANUAL_RULE_24_HOUR_JB_FARE: {
        if (this.isDepartureFromKorea(order)) {
          return `MMB.refund.rules.koreaRule24Hour.${notificationContext}.${this.notificationType}${contentFormat}`;
        }
        return `MMB.refund.rules.manualAndAutomaticRule24Hour.${notificationContext}.${this.notificationType}${contentFormat}`;
      }

      case FinnairCancelFlowType.AUTOMATIC_RULE_TICKET_SLIGHTCLASSIC:
      case FinnairCancelFlowType.MANUAL_RULE_TICKET_SLIGHTCLASSIC:
        return `MMB.refund.rules.manualAndAutomaticRuleSlightClassic.${notificationContext}.${this.notificationType}${contentFormat}`;

      case FinnairCancelFlowType.AUTOMATIC_RULE_TICKET_FLEX:
      case FinnairCancelFlowType.MANUAL_RULE_TICKET_FLEX:
        return `MMB.refund.rules.manualAndAutomaticRuleTicketFlex.${notificationContext}.${this.notificationType}${contentFormat}`;

      case FinnairCancelFlowType.AUTOMATIC_RULE_TICKET_OTHER:
      case FinnairCancelFlowType.MANUAL_RULE_TICKET_OTHER:
        return `MMB.refund.rules.manualAndAutomaticTicketOther.${notificationContext}.${this.notificationType}${contentFormat}`;

      case FinnairCancelFlowType.MANUAL_RULE_TICKET_AWARD:
        return `MMB.refund.rules.manualAndAutomaticTicketAward.${notificationContext}.${this.notificationType}${contentFormat}`;

      case FinnairCancelFlowType.RULE_CACR:
        return `MMB.refund.rules.cacr.${notificationContext}.${this.notificationType}${contentFormat}`;

      case FinnairCancelFlowType.MANUAL_INVOLUNTARY:
      case FinnairCancelFlowType.AUTOMATIC_INVOLUNTARY:
        return `MMB.refund.rules.manualAndAutomaticInvoluntary.${notificationContext}.${this.notificationType}${contentFormat}`;

      case FinnairCancelFlowType.MANUAL_DYNAMIC_WAIVER:
        return `MMB.fragments.voluntaryCancel.${notificationContext}.${this.notificationType}.manualDynamicWaiver${contentFormat}`;
      case FinnairCancelFlowType.AUTOMATIC_DYNAMIC_WAIVER:
        return `MMB.fragments.voluntaryCancel.${notificationContext}.${this.notificationType}.automaticDynamicWaiver${contentFormat}`;

      default: {
        flow satisfies never;
      }
    }
  };

  private isDepartureFromKorea(order: FinnairOrder): boolean {
    const departureLocationCode = order.bounds[0]?.departure?.locationCode;
    const locationData = order.locations?.[departureLocationCode];
    return locationData?.countryCode === KOREA_COUNTRY_CODE;
  }

  private displayEligibleForRefundMessage(flow: FinnairCancelFlowType): boolean {
    return (
      flow === FinnairCancelFlowType.AUTOMATIC_RULE_24_HOUR ||
      flow === FinnairCancelFlowType.MANUAL_RULE_24_HOUR ||
      flow === FinnairCancelFlowType.MANUAL_RULE_24_HOUR_JB_FARE
    );
  }
}
